/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'gemini/lib/@waca/dashboard-common/dist/core/APIFactory',
	'gemini/lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'baglass/utils/Utils',
	'storytelling/nls/StringResources',
	'../../StoryService',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/guided-journey-1_32',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/guided-journey-2_32',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/guided-journey-3_32',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/guided-journey-4_32',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/guided-journey-5_32',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/timesequence_32',
	'../../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/help_16'
], (_, APIFactory, PropertiesProviderAPI, GlassUtils, StringResources, StoryService,
	PanAndZoomLayout1, PanAndZoomLayout2, PanAndZoomLayout3, PanAndZoomLayout4, PanAndZoomLayout5, PanAndZoomLayout6, HelpIcon) => {

	const PAN_AND_ZOOM_ITEMS = [{
		name: 'panAndZoom1',
		label: StringResources.get('PanAndZoomLayout1'),
		type: 'svg',
		value: PanAndZoomLayout1.default.id
	}, {
		name: 'panAndZoom2',
		label: StringResources.get('PanAndZoomLayout2'),
		type: 'svg',
		value: PanAndZoomLayout2.default.id
	}, {
		name: 'panAndZoom3',
		label: StringResources.get('PanAndZoomLayout3'),
		type: 'svg',
		value: PanAndZoomLayout3.default.id
	}, {
		name: 'panAndZoom4',
		label: StringResources.get('PanAndZoomLayout4'),
		type: 'svg',
		value: PanAndZoomLayout4.default.id
	}, {
		name: 'panAndZoom5',
		label: StringResources.get('PanAndZoomLayout5'),
		type: 'svg',
		value: PanAndZoomLayout5.default.id
	}, {
		name: 'panAndZoom6',
		label: StringResources.get('PanAndZoomLayout6'),
		type: 'svg',
		value: PanAndZoomLayout6.default.id
	}];

	class StorytellingPropertiesProvider {
		constructor(options) {
			this.dashboardApi = options.dashboardApi || options.dashboardAPI;
			this._api = APIFactory.createAPI(this, [PropertiesProviderAPI]);
			// register self as a properties provider contributing storytelling properties
			this.dashboardApi.getFeature('Properties').registerProvider(this.getAPI());
		}

		getAPI() {
			return this._api;
		}

		getPropertyLayoutList() {
			return [{
				value: StringResources.get('storyProperties'),
				id: 'banner',
				type: 'Banner',
				editable: false
			}, {
				id: 'scenes',
				type: 'Section',
				label: StringResources.get('scenesPropertiesSection'),
				position: 0
			}];
		}

		getPropertyList() {
			return this._getScenesPropertiesList();
		}

		_getScenesPropertiesList() {
			const scenesProperties = [];
			scenesProperties.push(...this._getStoryPropertiesList());
			const storyType = this._getBoardModel().layout.type;
			if (storyType === 'slideshow') {
				scenesProperties.push(...this._getSlideShowPropertiesList());
			} else if (storyType.includes('panAndZoom')) {
				scenesProperties.push(...this._getPanAndZoomPropertiesList());
			}
			return scenesProperties;
		}

		_getBoardModel() {
			// TODO: temporary (short term) solution
			return this.dashboardApi.getFeature('internal').getBoardModel();
		}

		_getStoryPropertiesList() {
			let collapsiblePickerSelectedName;
			let currentLayout = this._getPropertyValueFromModel('type') || 'slideshow';
			if (currentLayout.indexOf('panAndZoom') != -1) {
				let index = (currentLayout.substring(currentLayout.length - 1, currentLayout.length) - 1);
				currentLayout = currentLayout.substring(0, currentLayout.length - 1);
				collapsiblePickerSelectedName = PAN_AND_ZOOM_ITEMS[index].name;
			}
			let collapsiblePickerOptions = {
				type: 'CollapsiblePicker',
				name: 'storyType',
				label: StringResources.get('layoutLabel'),
				selectedName: collapsiblePickerSelectedName,
				placeholder: {
					name: '',
					label: '',
					type: 'svg',
					value: HelpIcon.default.id
				},
				contentSize: 'large',
				items: PAN_AND_ZOOM_ITEMS,
				isRequired: true
			};
			let dropDownOptions = {
				name: 'storyType',
				label: StringResources.get('storyTypeLabel'),
				defaultValue: currentLayout,
				options: [{
					label: StringResources.get('slideshowLabel'),
					value: 'slideshow'
				}, {
					label: StringResources.get('panAndZoomLabel'),
					value: 'panAndZoom'
				}]
			};
			return [{
				id: 'storyType',
				getPropertyValue: () => currentLayout,
				setPropertyValue: (propertyValue) => {
					let value = propertyValue;
					if (propertyValue && propertyValue.name) {
						value = value.name;
					}
					let storyService = new StoryService({
						dashboardApi: this.dashboardApi
					});
					storyService.updateStory({
						boardModel: this._getBoardModel(),
						targetInfo: {
							type: value
						}
					});
				},
				editor: {
					sectionId: 'general.scenes',
					position: 1,
					uiControl: {
						type: 'TwoStageCombo',
						dropDownOptions: dropDownOptions,
						collapsiblePickerOptions: [
							null,
							collapsiblePickerOptions
						]
					}
				}
			}];
		}

		_getSlideShowPropertiesList() {
			return [{
				id: 'sceneTransition',
				getPropertyValue: () => this._getPropertyValueFromModel('transition'),
				setPropertyValue: (propertyValue) => {
					if (this._getBoardModel().layout.layoutPositioning !== 'relative') {
						this._updateStory(propertyValue);
					} else {
						this._changeTransition(propertyValue);
					}
				},
				editor: {
					sectionId: 'general.scenes',
					position: 2,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('sceneTransitionPropertyLabel'),
						options: [{
							label: StringResources.get('noSceneTransitionLabel'),
							value: 'none'
						},
						{
							label: StringResources.get('animatedPathSceneTransitionLabel'),
							value: 'animatedPath'
						},
						{
							label: StringResources.get('sweepSceneTransitionLabel'),
							value: 'scaleAndSlide'
						}
						],
						coachMark: {
							render: (options) => {
								GlassUtils.addCoachmark({
									id: 'com.ibm.bi.dashboard.storyProperties.animatedPath',
									title: StringResources.get('animatedPathCoachmarkTitle'),
									contents: StringResources.get('animatedPathCoachmarkContents'),
									placement: 'top',
									glassContext: options.glassContext,
									$el: options.$el
								});
							}
						}
					}
				}
			}];
		}

		_getPanAndZoomPropertiesList() {
			// the overview property name are very generic
			// to prevent clashing with other properties we add a prefix
			const propertyPrefix = 'overview_';

			const updateOverviewModel = (propertyName, propertyValue) => {
				const updateOptions = {
					updateArray: [{
						id: this._getBoardModel().layout.id,
						showOverviews: {
							[propertyName]: propertyValue
						}
					}]
				};
				this._getBoardModel().layout.updateModel(updateOptions, this, {
					undoRedoTransactionId: _.uniqueId('layoutShowOverview_')
				});
			};
			return [{
				id: propertyPrefix + 'showStart',
				getPropertyValue: () => this._getPropertyValueFromModel('showOverviews').showStart,
				setPropertyValue: (value) => {
					updateOverviewModel('showStart', value);
				},
				editor: {
					sectionId: 'general.scenes',
					position: 1,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propShowStoryOverviewFirstSlide')
					}
				}
			}, {
				id: propertyPrefix + 'showEnd',
				getPropertyValue: () => this._getPropertyValueFromModel('showOverviews').showEnd,
				setPropertyValue: (value) => {
					updateOverviewModel('showEnd', value);
				},
				editor: {
					sectionId: 'general.scenes',
					position: 2,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propShowStoryOverviewLastSlide')
					}
				}
			}];
		}

		_getPropertyValueFromModel(propName) {
			// TODO: temporary (short term) solution
			const model = this._getBoardModel().layout;
			return model.get(propName);
		}

		_updateStory(propertyValue) {
			const storyService = new StoryService({
				dashboardApi: this.dashboardApi
			});
			storyService.updateStory({
				boardModel: this._getBoardModel(),
				targetInfo: {
					type: 'slideshow',
					transition: propertyValue
				}
			});
		}

		_changeTransition(propertyValue) {
			const data = { undoRedoTransactionId: _.uniqueId('layout_transitionChange_') };
			this._getBoardModel().layout.set({ transition: propertyValue }, { sender: this, payloadData: data });
		}
	}
	return StorytellingPropertiesProvider;
});